<style scoped>
    .action-link {
        cursor: pointer;
    }
</style>

<template>
    <div>
        <div class="module-header">
            <h5 class="module-title">{{ $t('words.authorized_applications')}}</h5>
        </div>
        <div class="module-body">
            <div class="module-action-group" style="margin-bottom: 24px">
                <a :href="apiLink" title="Kollus Live API" target='_blank' class="btn btn-primary">
                    <i class="icon icon-link"></i>
                    {{ $t('words.go_kollus_live_api')}}
                </a>
                <nav aria-label="Page navigation" class="ml-auto">
                    <ul class="pagination">
                        <li class="page-info">
                            <span>
                            {{ $t('paragraphs.page_navigation', { total: meta.total, from: ((meta.current_page - 1) * meta.per_page + 1), to: (meta.current_page >= meta.last_page ? meta.total : meta.per_page * meta.current_page)}) }}
                            </span>
                        </li>
                        <li :class="{'page-item': true, disabled: meta.current_page <= 1 }">
                            <button class="page-link" aria-label="Previous" @click="moveCurrentPage(meta.current_page-1)" v-if="meta.current_page > 1">
                                <i class="icon icon-arrow-left-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.previous') }}</span>
                            </button>
                            <button class="page-link" aria-label="Previous" v-else>
                                <i class="icon icon-arrow-left-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.previous') }}</span>
                            </button>
                        </li>
                        <li :class="{'page-item': true, disabled: meta.last_page <= meta.current_page }">
                            <button class="page-link" aria-label="Next" @click="moveCurrentPage(meta.current_page+1)" v-if="meta.current_page < meta.last_page">
                                <i class="icon icon-arrow-right-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.next') }}</span>
                            </button>
                            <button class="page-link disabled" aria-label="Previous" disabled v-else>
                                <i class="icon icon-arrow-right-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.next') }}</span>
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>

            <div class="table__wide">
                <!-- Authorized Tokens -->
                <table class="table mb-0">
                    <thead>
                    <tr>
                        <th>{{ $t('words.name')}}</th>
                        <th>{{ $t('words.scopes')}}</th>
                        <th>{{ $t('words.create_date')}}</th>
                        <th>{{ $t('words.expire_date')}}</th>
                        <th></th>
                    </tr>
                    </thead>

                    <tbody v-if="tokens.length > 0">
                    <tr v-for="token in currentTokens">
                        <!-- Client Name -->
                        <td>
                            {{ token.client.name }}
                        </td>

                        <!-- Scopes -->
                        <td>
                            <span v-if="token.scopes.length > 0">
                                {{ token.scopes.join(', ') }}
                            </span>
                        </td>
                        <td>
                            {{ momentDate(token.created_at) }}
                        </td>
                        <td>
                            {{ momentDate(token.expires_at) }}
                        </td>

                        <!-- Revoke Button -->
                        <td class="td__action">
                            <a class="btn btn-icon btn-flat" v-if="haveSubmitRight" @click="revoke(token)">
                               <i class="icon icon-trash"></i>
                            </a>
                        </td>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr>
                        <td colspan="5" class="td__message">
                            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 24 24" class="svg-icon svg-icon-alert-circle"><g stroke-linecap="square" stroke-linejoin="miter" stroke-width="2" fill="#adb5bd" stroke="#adb5bd"><circle fill="none" stroke="#adb5bd" stroke-miterlimit="10" cx="12" cy="12" r="11"/> <line data-color="color-2" fill="none" stroke-miterlimit="10" x1="12" y1="7" x2="12" y2="13"/> <circle data-color="color-2" data-stroke="none" cx="12" cy="17" r="1" stroke-linejoin="miter" stroke-linecap="square" stroke="none"/></g></svg>
                            {{ $t('paragraphs.oauth_no_authorized_applications')}}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    /*
     * The component's data.
     */
    props: {
        haveSubmitRight: {
            type: [Boolean],
            require: true,
        },
    },
    data() {
        return {
            meta: {
                last_page: 0,
                current_page: 1,
                total: 0,
                per_page: 5,
            },
            tokens: [],
        };
    },

    /**
     * Prepare the component (Vue 1.x).
     */
    ready() {
        this.prepareComponent();
    },

    /**
     * Prepare the component (Vue 2.x).
     */
    mounted() {
        this.prepareComponent();
    },

    methods: {
        /**
         * Prepare the component (Vue 2.x).
         */
        moveCurrentPage(page) {
            this.meta.current_page = page;
        },
        prepareComponent() {
            this.getTokens();
        },

        /**
         * Get all of the authorized tokens for the user.
         */
        getTokens() {
            const self = this;
            axios.get('/oauth/tokens')
                .then((response) => {
                    this.tokens = response.data;
                    self.meta.total = self.tokens.length;
                    self.meta.last_page = parseInt(self.meta.total / self.meta.per_page, 10);
                    if (self.meta.total % self.meta.per_page !== 0) {
                        self.meta.last_page += 1;
                    }
                    self.meta.current_page = 1;
                });
        },

        /**
         * Revoke the given token.
         */
        revoke(token) {
            if (!this.haveSubmitRight) {
                this.$toasted.error(this.$t('paragraphs.error.check_login_right'));
                return;
            }
            const confirmMessage = this.$t('paragraphs.message.confirm.remove_item');
            if (confirm(confirmMessage)) {
                axios.delete(`/oauth/tokens/${token.id}`)
                    .then((response) => {
                        this.getTokens();
                    });
            }
        },
        momentDate(date) {
            return this.$moment(this.$moment.utc(`${date}`).toDate()).format('YYYY-MM-DD HH:mm:ss');
        },
    },
    computed: {
        currentTokens() {
            const start = (this.meta.current_page - 1) * this.meta.per_page;
            return _.slice(this.tokens, start, start + this.meta.per_page);
        },
        apiLink() {
            return `/api/docs/live-api/${(window.Laravel.lang === 'ja') ? 'jp' : 'ko'}`;
        },
    },
};
</script>
